import { clearOrderWizardOnLogout } from 'View/Page/OrderWizard/module.tsx'
import { useGetAuthenticatedUser } from 'Flux/Selector/index.js'
import { useDispatch, useSelector } from 'Flux/index.js'
import { selectHoverOverAvatar, setHoverOverAvatar } from 'Flux/Slice/Application/ApplicationSlice.js'

import { useInteraction } from 'Framework/View/Hooks/useInteraction.js'
import { isGlobalAdmin } from 'Model/User/IUser.js'
import { OrganizationOverrideIndicator } from './OrganizationOverrideIndicator.js'
import { useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { Environment } from 'environment.js'
import { SubscriptionBadge } from 'View/Common/Tier2Badge/Tier2Badge.js'
import type { IOrganization } from 'Model/index.js'
import { hasActiveSubscription } from 'Model/User/IUser.js'
import { UppyDashboardWidget } from '../../Component/UppyDashboardWidget.js'
import { useModal } from '../../Modal/UseModal.js'
import { HelpMenu } from './HelpMenu.js'

export function hasPaymentIssueWarning(org: IOrganization): boolean {
  return !!org.lastPaymentIssueAt && !org.isBillingMissing
}

export const TopBar = ({ drawerName }: { drawerName: string }) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()

  const authenticatedUser = useGetAuthenticatedUser()
  const { openModal } = useModal()
  const hoverOverAvatar = useSelector(selectHoverOverAvatar)

  const org = authenticatedUser?.org
  const showBillingBlocker = !!org?.isBillingMissing
  const showPaymentWarning = !!org?.lastPaymentIssueAt && !showBillingBlocker
  const userName = [authenticatedUser?.firstName, authenticatedUser?.lastName].filter(Boolean).join(' ')
  const showMarkusAdmin = !!authenticatedUser?.showAdminUx && isGlobalAdmin(authenticatedUser)

  const logout$ = useInteraction<void>()

  useEffect(() => {
    const logout$$ = logout$.subscribe(() => {
      clearOrderWizardOnLogout()
      window.location.href = `${Environment.API_URL}/oauth/logout`
    })
    return () => logout$$.unsubscribe()
  }, [logout$])

  const isHome = ['/', ''].includes(location.pathname)

  if (isHome) return null

  return (
    <div id="tour-navigation" className="navbar bg-base-100 border-b border-gray-300 px-2 sm:px-6 lg:px-8 shadow-sm z-30 h-16">
      {/* Mobile sidebar toggle button */}
      <div className="flex-none lg:hidden">
        <label htmlFor={drawerName} className="btn btn-square btn-ghost">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </label>
      </div>

      {/* Logo / Title */}
      <div className="flex flex-1 shrink-0 flex-row gap-4 items-center">
        <Link to="/home">
          <img src="/logo.png" alt="Spcfy GmbH" className="block h-8 w-auto" />
        </Link>

        <OrganizationOverrideIndicator />

        {showBillingBlocker && (
          <div className="flex items-center alert alert-error uppercase py-2 px-3">
            <Link to="/billing">
              <span>Billing issue: features may not work. Check your billing page.</span>
            </Link>
          </div>
        )}

        {showPaymentWarning && (
          <div className="flex items-center alert alert-warning uppercase py-2 px-3">
            <Link to="/billing">
              <span>A payment issue was detected. Your organization may soon be restricted. Check billing.</span>
            </Link>
          </div>
        )}
      </div>

      {/* Right side */}
      {authenticatedUser && (
        <div className="flex items-center gap-3 pr-2">
          <UppyDashboardWidget />

          <HelpMenu />

          {showMarkusAdmin && (
            <button className="btn btn-ghost normal-case" title="Open admin tools" onClick={() => openModal('markusAdmin', {})}>
              Admin
            </button>
          )}

          {/* Avatar + Dropdown */}
          <details
            id="tour-user"
            data-testid="user-dropdown"
            className="dropdown z-50 ml-2"
            open={hoverOverAvatar}
            onMouseEnter={() => dispatch(setHoverOverAvatar(true))}
            onMouseLeave={() => dispatch(setHoverOverAvatar(false))}
          >
            <summary
              role="button"
              className="btn m-1 flex rounded-full bg-white-800 text-sm hover:outline-hidden hover:ring-2 hover:ring-white hover:ring-offset-2 hover:ring-offset-gray-800 relative"
            >
              <img className="h-8 w-8 rounded-full" src="/profile.png" alt="Profile" />
              {userName && (
                <span data-testid="user-name" className="hidden md:inline text-sm font-semibold normal-case">
                  {userName}
                </span>
              )}
              <div className="flex flex-col gap-1">
                {authenticatedUser.org.chargebeePlanId && hasActiveSubscription(authenticatedUser.org) && (
                  <SubscriptionBadge planId={authenticatedUser.org.chargebeePlanId} orientation="relative" />
                )}
              </div>
            </summary>
            <ul
              id="tour-user-menu"
              className="dropdown-content menu absolute right-0 z-10 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-hidden"
            >
              <li>
                <a href={`${authenticatedUser.authServer}/account`} target="_blank" rel="noreferrer">
                  Manage User Profile
                </a>
              </li>
              {authenticatedUser?.org?.id && (
                <li data-testid="org-menu">
                  <a
                    href="#"
                    onClick={() => navigate(`/organization/${authenticatedUser.org.id}`)}
                    className="block px-4 py-2 text-sm text-gray-700"
                  >
                    My Organization
                  </a>
                </li>
              )}
              <li>
                <Link to="/billing" className="block px-4 py-2 text-sm text-gray-700">
                  Billing
                </Link>
              </li>
              <li>
                <a href="#" onClick={() => logout$.next()} className="block px-4 py-2 text-sm text-gray-700">
                  Logout
                </a>
              </li>
            </ul>
          </details>
        </div>
      )}
    </div>
  )
}
